import type { useTranslation } from 'react-i18next'
import { PipelineApiError } from '@/lib/api/pipelines.ts'
import { refusedWriteMessage } from '@/shared/hooks/usePipelineAdapter.ts'

type TFn = ReturnType<typeof useTranslation>['t']

/** Normalize any thrown value into a typed `PipelineApiError`. */
export function asApiError(err: unknown): PipelineApiError {
  return err instanceof PipelineApiError
    ? err
    : new PipelineApiError({ kind: 'unknown', status: null, message: 'save failed' })
}

/**
 * Maps a `PipelineApiError.kind` onto a user-facing message for lead-edit
 * surfaces (Properties inline edits + the Move-stage dialog). Mirrors
 * `NewLeadDialog.submitErrorMessage` but under the `leads.detail` namespace.
 */
export function editErrorMessage(err: PipelineApiError, t: TFn): string {
  const refused = refusedWriteMessage(t, err)
  if (refused !== null) return refused
  switch (err.kind) {
    case 'unauthorized':
      return t('pages.leads.detail.properties.errors.unauthorized', {
        defaultValue: 'Session expired — refresh the page and try again.',
      })
    case 'not_found':
      return t('pages.leads.detail.properties.errors.notFound', {
        defaultValue: 'This lead, stage, or owner is no longer available. Reload to refresh.',
      })
    case 'network':
      return t('pages.leads.detail.properties.errors.network', {
        defaultValue: 'Network error — check your connection and retry.',
      })
    default:
      return t('pages.leads.detail.properties.errors.generic', {
        defaultValue: 'Could not save the change. Please try again.',
      })
  }
}
